import { Box, Card, Chip, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { useDialog } from "lib/@hooks/useDialog"
import Iconify from "minimals-template/components/Iconify"
import { ComponentDetailsDialog } from "./component-details-dialog"
import { BaseComponentSummaryValue } from "./base-component-summary-value"
import { COLOR_FROM_CLASSIFICATION } from "library/SFG20/constants.mjs"
import { useSkill } from "routes/@lib/skills"

export function BaseComponentCard({ component, sx = {} }) {
    const openDetails = useDialog(<ComponentDetailsDialog task={component} />, { maxWidth: "lg", fullWidth: true })
    const skill = useSkill(component?.skillSet)

    if (!component) return null

    return (
        <Card data-cy="base-component-card" sx={{ p: 3, display: "flex", ...sx }}>
            <Stack spacing={1.5} sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="h6">Base Component</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={`v${component.componentVersion ?? 1}`} size="small" variant="outlined" />
                        <Tooltip title="View base component details">
                            <IconButton
                                size="small"
                                onClick={openDetails}
                                data-cy="view-base-component-details"
                                aria-label="View base component details"
                            >
                                <Iconify icon="mdi:eye-outline" width={20} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
                <Box
                    sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.5,
                        bgcolor: "action.hover",
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                    }}
                >
                    <Typography variant="h6">{component.title}</Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack direction={{ xs: "column", md: "row" }} alignItems="stretch" sx={{ flex: 1 }}>
                        <BaseComponentSummaryValue
                            label="Classification"
                            value={component.classification}
                            icon="mdi:circle"
                            color={COLOR_FROM_CLASSIFICATION[component.classification]}
                            sx={{ flex: 1, py: { xs: 1.5, md: 0 }, pl: { xs: 0, md: 0 }, pr: { xs: 0, md: 2 } }}
                        />
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />
                        <BaseComponentSummaryValue
                            label="Frequency"
                            value={formatFrequency(component.frequency)}
                            icon="mdi:calendar-sync-outline"
                            palette="primary"
                            sx={{ flex: 1, py: { xs: 1.5, md: 0 }, px: { xs: 0, md: 2 } }}
                        />
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />
                        <BaseComponentSummaryValue
                            label="Skill Set"
                            value={skill?.Skilling ?? component.skillSet}
                            icon="mdi:account-hard-hat-outline"
                            palette="info"
                            sx={{ flex: 1, py: { xs: 1.5, md: 0 }, px: { xs: 0, md: 2 } }}
                        />
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />
                        <BaseComponentSummaryValue
                            label="Timing"
                            value={formatTiming(component.minutes)}
                            icon="mdi:timer-outline"
                            palette="secondary"
                            sx={{ flex: 1, py: { xs: 1.5, md: 0 }, pl: { xs: 0, md: 2 }, pr: { xs: 0, md: 0 } }}
                        />
                    </Stack>
                </Box>
            </Stack>
        </Card>
    )
}

function formatFrequency(frequency = {}) {
    if (frequency?.label) return frequency.label
    const period = frequency?.period?.[0]
    if (!period) return null
    if (period === "U") return `${frequency?.interval ?? 0}U`
    return `Every ${frequency?.interval ?? 0}${period}`
}

function formatTiming(minutes) {
    if (minutes === undefined || minutes === null) return null
    return `${minutes}m`
}
